import React from 'react';
import './_ScopeStatusPopover.scss';
import { Button, ButtonGroup, Tooltip, IconButton } from '@material-ui/core';
import { openScopeSelectQaSelector } from '../MfpScopebar.types';
import {
  CONTEXT_PENDING,
  CONTEXT_READY,
  CONTEXT_FAILED,
  CONTEXT_STATUSES,
  CONTEXT_BUSY,
} from 'src/state/workingSets/workingSets.types';
import { WorkingSetContext } from 'src/state/workingSets/workingSets.slice';
import { DEFAULT_DIMENSIONS } from 'src/utils/Domain/Constants';
import ScopeText from '../MfpScopeText';
import { SCOPECREATE_WITHOUT_WP } from 'src/services/Scope.client';
import { Space } from 'src/space';
import { ServerScopeMember } from 'src/state/scope/Scope.types';
import { isNil } from 'lodash';
import CloseIcon from '@material-ui/icons/Close';

type ScopeStatusPopoverProps = {
  labelDimension: Space<keyof ServerScopeMember> | undefined;
  scopeId: string | undefined;
  pendingWrites: number;
  scopeContexts: WorkingSetContext[];
  onScopeClick: () => void;
  handleUpdateScopeInfo: (scopeId: string, module: string) => void;
  deleteScope: (scopeId: string, contexts: WorkingSetContext[]) => void;
};

const ScopeStatusPopover = (props: ScopeStatusPopoverProps) => {
  const {
    scopeId,
    scopeContexts,
    onScopeClick,
    handleUpdateScopeInfo,
    labelDimension,
    pendingWrites,
    deleteScope,
  } = props;

  const sortedContexts = [...scopeContexts].sort(sortByScopeCreationTime);

  const renderStatusIcon = () => {
    const currentScope = sortedContexts.find((scope) => scope.id === scopeId);
    const scopeStatus = currentScope ? currentScope.status : undefined;

    if (pendingWrites > 0) {
      return <i className="far fa-fw fa-spinner-third fa-spin" />;
    }

    switch (scopeStatus) {
      case 'busy':
        return <i className="fa fa-fw fa-circle-notch fa-spin" />;
      case 'pending':
        return <i className="fa fa-fw fa-spinner fa-spin" />;
      case 'failed':
        return <i className="far fa-fw fa-exclamation-triangle" />;
      default:
        return <i className="far fa-fw fa-crosshairs" />;
    }
  };

  const tooltipContent = (
    <div className="scope-popover-container">
      <h5>Your current scopes: </h5>
      <ButtonGroup
        orientation="vertical"
        color="default"
        aria-label="vertical outlined button group"
        className="scope-button-group"
      >
        {sortedContexts.map((context, idx) => {
          const scopeText = DEFAULT_DIMENSIONS.map((dim) => {
            const array = Object.values(context.anchor[dim]);
            return array
              .map((x) => (!isNil(labelDimension) ? ` ${x[labelDimension[dim]]}` : ` ${x['id']}`))
              .join('');
          }).join(' | ');

          if (context.initParams.type === SCOPECREATE_WITHOUT_WP) {
            return <div key={idx} />;
          }

          return (
            <div
              className={`scope-status-row ${context.id === scopeId ? 'current' : ''}`}
              key={context.id}
            >
              <Button
                className="scope-status-button"
                onClick={() => handleUpdateScopeInfo(context.id, context.module)}
              >
                {contextStateToIcon(context.status)}
                <ScopeText content={scopeText} />
              </Button>
              <Tooltip
                title="Remove this scope from your session (data will not be lost)"
                placement="top"
                classes={{
                  tooltip: 'scope-status-popover-tooltip',
                }}
              >
                <IconButton
                  className="scope-status-delete"
                  onClick={() => deleteScope(context.id, sortedContexts)}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          );
        })}
      </ButtonGroup>
    </div>
  );

  return (
    <li className="scope-options-item scope-status-popover">
      <Tooltip
        title={tooltipContent}
        placement="bottom"
        interactive
        enterDelay={150}
        leaveDelay={150}
        classes={{ tooltip: 'scope-status-popover-root' }}
      >
      <button
        className="scope-options-button"
        data-qa={openScopeSelectQaSelector}
        onClick={onScopeClick}
        type="button"
      >
          {renderStatusIcon()}
          Scope
        </button>
      </Tooltip>
    </li>
  );
};

export default ScopeStatusPopover;

const statusToIconMap = {
  [CONTEXT_READY]: 'far fa-check-circle',
  [CONTEXT_PENDING]: 'fas fa-spinner fa-spin',
  [CONTEXT_BUSY]: 'fas fa-spinner fa-spin',
  [CONTEXT_FAILED]: 'far fa-exclamation-triangle',
};

const contextStateToIcon = (contextState: CONTEXT_STATUSES): JSX.Element => {
  return <i className={`scope-status-icon ${statusToIconMap[contextState]}`} />;
};

const sortByScopeCreationTime = (lScope: WorkingSetContext, rScope: WorkingSetContext) => {
  return lScope.contextCreationTime.unix() - rScope.contextCreationTime.unix();
};
